import { iconFromEnum, labelFromEnum } from '@/lib/format';
import { cn } from '@/lib/cn';
import {
  campaignStatusTone,
  channelTone,
  churnRiskTone,
  customerTierTone,
  kpiStatusTone,
  neutralTone,
  priorityTone,
  ticketStatusTone,
  ticketTypeTone,
  warrantyStatusTone,
  type ToneClasses,
} from '@/lib/tone-classes';
import type {
  CampaignStatus,
  ChurnRisk,
  ContactChannel,
  CustomerStatus,
  CustomerTier,
  FollowupStatus,
  FollowupType,
  KpiStatus,
  PartRequestStatus,
  TicketPriority,
  TicketStatus,
  TicketType,
  WarrantyStatus,
} from '@/graphql/generated';
import { domainIcon } from './icons';

export type BadgeSize = 'sm' | 'md' | 'lg';

const sizeClass: Record<BadgeSize, string> = {
  sm: 'gap-1 px-1.5 py-0.5 text-2xs [&_svg]:size-3',
  md: 'gap-1 px-2 py-0.5 text-xs [&_svg]:size-3.5',
  lg: 'gap-1.5 px-2.5 py-1 text-sm [&_svg]:size-4',
};

/**
 * Base de todos los badges de dominio.
 *
 * Renderiza SIEMPRE icono + etiqueta + color. El color por sí solo no
 * comunica: quien no distingue el violeta de «escalado» del verde de
 * «resuelto» lo lee por el icono y por el texto.
 */
export function DomainBadge({
  tone,
  icon,
  label,
  size = 'md',
  variant = 'solid',
  className,
  title,
}: {
  tone: ToneClasses;
  icon: string;
  label: string;
  size?: BadgeSize;
  /** `outline` deja el fondo transparente: es el tratamiento de «cerrado». */
  variant?: 'solid' | 'outline';
  className?: string;
  title?: string;
}) {
  const Icon = domainIcon(icon);
  return (
    <span
      title={title}
      className={cn(
        'inline-flex max-w-full items-center whitespace-nowrap rounded-full font-medium ring-1 ring-inset',
        sizeClass[size],
        variant === 'solid' ? tone.bg : 'bg-transparent',
        tone.fg,
        tone.ring,
        className,
      )}
    >
      <Icon aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}

// ── Badges concretos ────────────────────────────────────────────────────────

export function StatusBadge({
  status,
  size,
  className,
}: {
  status: TicketStatus;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={ticketStatusTone[status] ?? neutralTone}
      icon={iconFromEnum('ticketStatus', status)}
      label={labelFromEnum('ticketStatus', status)}
      // «Cerrado» va en contorno y «pendiente» en relleno: es lo único que los
      // separa además del icono, porque los dos son neutros.
      variant={status === 'CERRADO' ? 'outline' : 'solid'}
      size={size}
      className={className}
    />
  );
}

export function PriorityBadge({
  priority,
  size,
  className,
}: {
  priority: TicketPriority;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={priorityTone[priority] ?? neutralTone}
      icon={iconFromEnum('priority', priority)}
      label={labelFromEnum('priority', priority)}
      size={size}
      className={className}
    />
  );
}

export function TypeBadge({
  type,
  size,
  className,
}: {
  type: TicketType;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={ticketTypeTone[type] ?? neutralTone}
      icon={iconFromEnum('ticketType', type)}
      label={labelFromEnum('ticketType', type)}
      size={size}
      className={className}
    />
  );
}

export function ChannelBadge({
  channel,
  size,
  className,
  iconOnly,
}: {
  channel: ContactChannel;
  size?: BadgeSize;
  className?: string;
  /** En tablas densas el canal cabe mejor como icono con etiqueta accesible. */
  iconOnly?: boolean;
}) {
  const tone = channelTone[channel] ?? neutralTone;
  const label = labelFromEnum('channel', channel);
  const Icon = domainIcon(iconFromEnum('channel', channel));

  if (iconOnly) {
    return (
      <span
        className={cn('inline-flex items-center justify-center rounded-full p-1', tone.bg, tone.fg, className)}
        title={label}
      >
        <Icon className="size-3.5" aria-hidden="true" />
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <DomainBadge
      tone={tone}
      icon={iconFromEnum('channel', channel)}
      label={label}
      size={size}
      className={className}
    />
  );
}

export function TierBadge({
  tier,
  size,
  className,
}: {
  tier: CustomerTier;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={customerTierTone[tier] ?? neutralTone}
      icon={iconFromEnum('customerTier', tier)}
      label={labelFromEnum('customerTier', tier)}
      size={size}
      className={className}
    />
  );
}

export function ChurnRiskBadge({
  risk,
  score,
  size,
  className,
}: {
  risk: ChurnRisk;
  /** Probabilidad 0..1: se muestra como título para no cargar el badge. */
  score?: number | null;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={churnRiskTone[risk] ?? neutralTone}
      icon={iconFromEnum('churnRisk', risk)}
      label={labelFromEnum('churnRisk', risk)}
      size={size}
      className={className}
      title={score != null ? `Probabilidad de abandono: ${Math.round(score * 100)} %` : undefined}
    />
  );
}

export function WarrantyStatusBadge({
  status,
  daysRemaining,
  size,
  className,
}: {
  status: WarrantyStatus;
  daysRemaining?: number | null;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={warrantyStatusTone[status] ?? neutralTone}
      icon={iconFromEnum('warrantyStatus', status)}
      label={labelFromEnum('warrantyStatus', status)}
      size={size}
      className={className}
      title={
        daysRemaining != null
          ? daysRemaining >= 0
            ? `Faltan ${daysRemaining} días`
            : `Venció hace ${Math.abs(daysRemaining)} días`
          : undefined
      }
    />
  );
}

export function KpiStatusBadge({
  status,
  size,
  className,
}: {
  status: KpiStatus;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={kpiStatusTone[status] ?? neutralTone}
      icon={iconFromEnum('kpiStatus', status)}
      label={labelFromEnum('kpiStatus', status)}
      size={size}
      className={className}
    />
  );
}

export function CampaignStatusBadge({
  status,
  size,
  className,
}: {
  status: CampaignStatus;
  size?: BadgeSize;
  className?: string;
}) {
  return (
    <DomainBadge
      tone={campaignStatusTone[status] ?? neutralTone}
      icon={iconFromEnum('campaignStatus', status)}
      label={labelFromEnum('campaignStatus', status)}
      // «Cancelada» va en contorno, como «cerrado»: es historia, no un estado
      // que reclame atención. Su token declara el fondo transparente.
      variant={status === 'CANCELADA' ? 'outline' : 'solid'}
      size={size}
      className={className}
    />
  );
}

// ── Estados sin token propio ────────────────────────────────────────────────
// Estos enums existen en el contrato pero no tienen entrada en `domain.ts`.
// Se resuelven aquí con tokens SEMÁNTICOS (nunca con colores crudos) y quedan
// anotados en el informe para que el paquete de tokens los incorpore.

const semanticTone = {
  neutral: neutralTone,
  info: {
    bg: 'bg-info-subtle',
    fg: 'text-info-subtle-fg',
    border: 'border-info-border',
    dot: 'bg-info',
    ring: 'ring-info-border',
  },
  success: {
    bg: 'bg-success-subtle',
    fg: 'text-success-subtle-fg',
    border: 'border-success-border',
    dot: 'bg-success',
    ring: 'ring-success-border',
  },
  warning: {
    bg: 'bg-warning-subtle',
    fg: 'text-warning-subtle-fg',
    border: 'border-warning-border',
    dot: 'bg-warning',
    ring: 'ring-warning-border',
  },
  danger: {
    bg: 'bg-danger-subtle',
    fg: 'text-danger-subtle-fg',
    border: 'border-danger-border',
    dot: 'bg-danger',
    ring: 'ring-danger-border',
  },
  primary: {
    bg: 'bg-primary-subtle',
    fg: 'text-primary-subtle-fg',
    border: 'border-primary-border',
    dot: 'bg-primary',
    ring: 'ring-primary-border',
  },
} satisfies Record<string, ToneClasses>;

const CUSTOMER_STATUS: Record<CustomerStatus, { label: string; icon: string; tone: ToneClasses }> = {
  ACTIVO: { label: 'Activo', icon: 'CircleCheck', tone: semanticTone.success },
  INACTIVO: { label: 'Inactivo', icon: 'CircleDashed', tone: semanticTone.neutral },
  POTENCIAL: { label: 'Potencial', icon: 'Eye', tone: semanticTone.info },
  PERDIDO: { label: 'Perdido', icon: 'CircleX', tone: semanticTone.danger },
};

export function CustomerStatusBadge({
  status,
  size,
  className,
}: {
  status: CustomerStatus;
  size?: BadgeSize;
  className?: string;
}) {
  const meta = CUSTOMER_STATUS[status];
  return <DomainBadge tone={meta.tone} icon={meta.icon} label={meta.label} size={size} className={className} />;
}

const FOLLOWUP_STATUS: Record<FollowupStatus, { label: string; icon: string; tone: ToneClasses }> = {
  PROGRAMADO: { label: 'Programado', icon: 'CalendarClock', tone: semanticTone.info },
  CONFIRMADO: { label: 'Confirmado', icon: 'CircleCheck', tone: semanticTone.primary },
  EN_CURSO: { label: 'En curso', icon: 'LoaderCircle', tone: semanticTone.warning },
  REALIZADO: { label: 'Realizado', icon: 'CircleCheck', tone: semanticTone.success },
  NO_REALIZADO: { label: 'No realizado', icon: 'CircleX', tone: semanticTone.danger },
  CANCELADO: { label: 'Cancelado', icon: 'Ban', tone: semanticTone.neutral },
};

export function FollowupStatusBadge({
  status,
  size,
  className,
}: {
  status: FollowupStatus;
  size?: BadgeSize;
  className?: string;
}) {
  const meta = FOLLOWUP_STATUS[status];
  return <DomainBadge tone={meta.tone} icon={meta.icon} label={meta.label} size={size} className={className} />;
}

const FOLLOWUP_TYPE: Record<FollowupType, { label: string; icon: string }> = {
  LLAMADA: { label: 'Llamada', icon: 'Phone' },
  VISITA_TECNICA: { label: 'Visita técnica', icon: 'Wrench' },
  MANTENIMIENTO: { label: 'Mantenimiento', icon: 'Cog' },
  ENCUESTA: { label: 'Encuesta', icon: 'ChartNoAxesCombined' },
  RECORDATORIO: { label: 'Recordatorio', icon: 'CalendarClock' },
};

export function FollowupTypeBadge({
  type,
  size,
  className,
}: {
  type: FollowupType;
  size?: BadgeSize;
  className?: string;
}) {
  const meta = FOLLOWUP_TYPE[type];
  return <DomainBadge tone={neutralTone} icon={meta.icon} label={meta.label} size={size} className={className} />;
}

export function followupTypeLabel(type: FollowupType): string {
  return FOLLOWUP_TYPE[type].label;
}

const PART_REQUEST_STATUS: Record<PartRequestStatus, { label: string; icon: string; tone: ToneClasses }> = {
  SOLICITADO: { label: 'Solicitado', icon: 'Inbox', tone: semanticTone.info },
  COTIZADO: { label: 'Cotizado', icon: 'FilePen', tone: semanticTone.warning },
  APROBADO: { label: 'Aprobado', icon: 'CircleCheck', tone: semanticTone.primary },
  EN_TRANSITO: { label: 'En tránsito', icon: 'Send', tone: semanticTone.warning },
  ENTREGADO: { label: 'Entregado', icon: 'CircleCheck', tone: semanticTone.success },
  RECHAZADO: { label: 'Rechazado', icon: 'CircleX', tone: semanticTone.danger },
  CANCELADO: { label: 'Cancelado', icon: 'Ban', tone: semanticTone.neutral },
};

export function PartRequestStatusBadge({
  status,
  size,
  className,
}: {
  status: PartRequestStatus;
  size?: BadgeSize;
  className?: string;
}) {
  const meta = PART_REQUEST_STATUS[status];
  return <DomainBadge tone={meta.tone} icon={meta.icon} label={meta.label} size={size} className={className} />;
}

export function partRequestStatusLabel(status: PartRequestStatus): string {
  return PART_REQUEST_STATUS[status].label;
}

export function customerStatusLabel(status: CustomerStatus): string {
  return CUSTOMER_STATUS[status].label;
}

export function followupStatusLabel(status: FollowupStatus): string {
  return FOLLOWUP_STATUS[status].label;
}
